import type { ConfigScopeRef } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { getCrowdsecScenarios } from '../api/crowdsec.js';
import { SelectField, TextAreaField } from '../ui/Field.js';
import { BAN_LENGTHS, type Draft } from './block-rule-text.js';

/**
 * What a CrowdSec trigger asks for (P11.16): which scenarios, and what CrowdSec does
 * when one fires. The scenarios that have fired where this rule applies are offered
 * with what became of their alerts — most useful for the ones that never ban, which
 * a trigger can turn into a ban — so a name does not have to be remembered.
 */

const count = (value: number): string => value.toLocaleString('en-US');

const linesOf = (text: string): string[] => text.split(/[\s,]+/).filter((each) => each !== '');

export function TriggerFields({
  draft,
  set,
  scope,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  scope: ConfigScopeRef;
}): ReactNode {
  const scenarios = useQuery({
    queryKey: ['crowdsec-scenarios', scope],
    queryFn: () =>
      getCrowdsecScenarios({
        ...(scope.scope === 'server' ? { serverId: scope.id } : {}),
        ...(scope.scope === 'group' ? { groupId: scope.id } : {}),
      }),
  });
  const chosen = new Set(linesOf(draft.scenarios));
  const add = (name: string): void => {
    if (chosen.has(name)) return;
    set({ scenarios: [...chosen, name].join('\n') });
  };
  const offered = (scenarios.data?.scenarios ?? []).filter((each) => each.triggerable);

  return (
    <>
      <TextAreaField
        label="Scenarios"
        hint="One per line, as author/name (for example crowdsecurity/ssh-time-based-bf). When any of them fires on a server, CrowdSec does what is chosen below."
        rows={3}
        value={draft.scenarios}
        onChange={(event) => {
          set({ scenarios: event.target.value });
        }}
      />

      <fieldset className="fieldset">
        <legend>Seen on the servers this rule reaches, last 30 days</legend>
        {scenarios.isPending && <p className="muted text-sm">Looking…</p>}
        {scenarios.data !== undefined && offered.length === 0 && (
          <p className="muted text-sm">
            No scenario has raised an alert there yet. Type a name above; CrowdSec ignores one it
            does not run.
          </p>
        )}
        <ul className="scenario-list">
          {offered.slice(0, 12).map((each) => (
            <li key={each.name}>
              <button
                type="button"
                className="scenario-pick"
                disabled={chosen.has(each.name)}
                onClick={() => {
                  add(each.name);
                }}
              >
                <code>{each.name}</code>
                <span className="cell-sub">
                  {count(each.alerts)} alert{each.alerts === 1 ? '' : 's'} from{' '}
                  {count(each.addresses)} address{each.addresses === 1 ? '' : 'es'} ·{' '}
                  {each.banned === 0 ? (
                    <strong>never banned: alert only</strong>
                  ) : (
                    `${count(each.banned)} came with a ban`
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset className="fieldset">
        <legend>What CrowdSec does</legend>
        <label className="checkbox">
          <input
            type="radio"
            name="trigger-response"
            checked={draft.triggerResponse === 'ban'}
            onChange={() => {
              set({ triggerResponse: 'ban' });
            }}
          />
          Ban the address (the firewall blocks it, or the web layer where one is installed)
        </label>
        <label className="checkbox">
          <input
            type="radio"
            name="trigger-response"
            checked={draft.triggerResponse === 'captcha'}
            onChange={() => {
              set({ triggerResponse: 'captcha' });
            }}
          />
          Challenge it with a captcha — only where the server&apos;s web layer can show one;
          anywhere else it does nothing
        </label>
      </fieldset>

      <SelectField
        label={draft.triggerResponse === 'ban' ? 'Ban for' : 'Challenge for'}
        value={draft.ban}
        onChange={(event) => {
          set({ ban: event.target.value });
        }}
      >
        {BAN_LENGTHS.map((each) => (
          <option key={each.seconds} value={String(each.seconds)}>
            {each.label}
          </option>
        ))}
      </SelectField>
    </>
  );
}
